import { createSelector } from '@reduxjs/toolkit';
import { FeatureSelectorWithFallback } from '@store/storeTypes';
import { RootState } from '@store/types';
import { ActionUIState } from './slice';

// Default state for when the slice is not loaded
const emptyActionUI: ActionUIState = {
  expandedCardId: null,
  rejectionModal: {
    isOpen: false,
    context: null,
  },
  acceptModal: {
    isOpen: false,
    context: null,
  },
  editModal: {
    isOpen: false,
    context: null,
  },
  pendingCommand: null,
  drafts: {},
  histories: {},
};

//----------------------------------------------------------------------
// SELECTORS
//----------------------------------------------------------------------

// Base selector - with fallback for when feature is not registered
export const selectActionUIState: FeatureSelectorWithFallback<
  ActionUIState,
  typeof emptyActionUI
> = (state: RootState) => state['actions/ui'] || emptyActionUI;

//----------------------------------------------------------------------
// 1. Basic property selectors
//----------------------------------------------------------------------

export const selectExpandedCardId = createSelector(
  [selectActionUIState],
  (actionUI): string | null => actionUI.expandedCardId,
);

export const selectRejectionModalState = createSelector(
  [selectActionUIState],
  actionUI => actionUI.rejectionModal,
);

export const selectIsRejectionModalOpen = createSelector(
  [selectRejectionModalState],
  (modal): boolean => modal.isOpen,
);

export const selectRejectionModalContext = createSelector(
  [selectRejectionModalState],
  modal => modal.context,
);

export const selectAcceptModalState = createSelector(
  [selectActionUIState],
  actionUI => actionUI.acceptModal,
);

export const selectIsAcceptModalOpen = createSelector(
  [selectAcceptModalState],
  (modal): boolean => modal.isOpen,
);

export const selectAcceptModalContext = createSelector(
  [selectAcceptModalState],
  modal => modal.context,
);

export const selectEditModalState = createSelector(
  [selectActionUIState],
  actionUI => actionUI.editModal,
);

export const selectIsEditModalOpen = createSelector(
  [selectEditModalState],
  (modal): boolean => modal.isOpen,
);

export const selectEditModalContext = createSelector(
  [selectEditModalState],
  modal => modal.context,
);

//----------------------------------------------------------------------
// 2. Derived data selectors
//----------------------------------------------------------------------

export const selectIsAnyCardExpanded = createSelector(
  [selectExpandedCardId],
  (expandedCardId): boolean => expandedCardId !== null,
);

//----------------------------------------------------------------------
// 3. Parameterized selector factories
//----------------------------------------------------------------------

export const makeSelectIsCardExpanded = (id: string) =>
  createSelector([selectExpandedCardId], (expandedCardId): boolean => expandedCardId === id);

//----------------------------------------------------------------------
// 4. Draft selectors
//----------------------------------------------------------------------

export const selectDraftForAction = (actionId: string) => (state: RootState) => {
  const actionUI = selectActionUIState(state);
  return actionUI.drafts?.[actionId] || null;
};

export const selectHistoryForAction = (actionId: string) => (state: RootState) => {
  const actionUI = selectActionUIState(state);
  return actionUI.histories?.[actionId] || null;
};

export const selectCanUndo = (actionId: string) =>
  createSelector([(state: RootState) => selectHistoryForAction(actionId)(state)], history =>
    history ? history.past.length > 0 : false,
  );

export const selectCanRedo = (actionId: string) =>
  createSelector([(state: RootState) => selectHistoryForAction(actionId)(state)], history =>
    history ? history.future.length > 0 : false,
  );

// Selector to get all active drafts (useful for cleanup)
export const selectAllDrafts = createSelector([selectActionUIState], actionUI => actionUI.drafts);

// Selector to check if any action has unsaved drafts
export const selectHasUnsavedDrafts = createSelector([selectAllDrafts], drafts =>
  Object.values(drafts).some(draft => draft.isDirty),
);

//----------------------------------------------------------------------
// 5. Generic typed selectors for type-safe draft access
//----------------------------------------------------------------------

/**
 * Creates a type-safe selector for a specific action type's draft
 * Usage: const selectPepCDraft = makeSelectTypedDraftForAction<PepCPayload>('action123');
 */
export function makeSelectTypedDraftForAction<T extends Record<string, any>>(actionId: string) {
  return createSelector(
    [(state: RootState) => selectDraftForAction(actionId)(state)],
    (draft): { actionId: string; data: T; lastSaved: number; isDirty: boolean } | null => {
      if (!draft) return null;
      return draft as { actionId: string; data: T; lastSaved: number; isDirty: boolean };
    },
  );
}

/**
 * Creates a type-safe selector for a specific action type's history
 * Usage: const selectPepCHistory = makeSelectTypedHistoryForAction<PepCPayload>('action123');
 */
export function makeSelectTypedHistoryForAction<T extends Record<string, any>>(actionId: string) {
  return createSelector(
    [(state: RootState) => selectHistoryForAction(actionId)(state)],
    (
      history,
    ): {
      past: Array<{ data: T; timestamp: number }>;
      present: { data: T; timestamp: number } | null;
      future: Array<{ data: T; timestamp: number }>;
    } | null => {
      if (!history) return null;
      return history as {
        past: Array<{ data: T; timestamp: number }>;
        present: { data: T; timestamp: number } | null;
        future: Array<{ data: T; timestamp: number }>;
      };
    },
  );
}
